import { Spinner } from '@/components/common'
import { StandardSuccessComponent, type ProceedToRedeemContext } from '@/components/success'
import ExternalRedeemModal from '@/pages/external/redeem/ExternalRedeemModal'
import { RevenueCatPurchase } from '@/pages/external/RevenuecatCheckout'
import { eventTracker, trackPurchaseCompleted } from '@/config/trackingFunctions'
import { APP_CUSTOM_ERROR, APP_TIMEOUT_ERROR } from '@/constants/MessageConstants'
import { PAGE_NAMES, TRACKING_TYPES } from '@/constants/TrackingConstants'
import { ErrorBoundary, useErrorBoundary } from 'react-error-boundary'

import {
  useGrantConsent,
  useLoginExternalApplication,
  useResendTwoFactor,
  useVerifyTwoFactor,
  useOauthSubscriptions,
} from '@/services'
import {
  findTwoFactorChallenge,
  hasPasswordReset,
  hasStrongMfaSetup,
  requirementsFrom,
  SECOND_FACTOR_URN,
  SecondFactorKind,
} from '@/types'
import { Dialog } from '@headlessui/react'
import ExternalSignInValidationSchema from '@/utils/schema/ExternalSignInValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import { ROUTE_FORGOT_PASSWORD, ROUTE_TWO_FACTOR } from '@/constants/Routes'
import { BASE_APP_URL } from '@/constants/Common'
import { isEmbeddedContext } from '@/utils/embeddedContext'
import { Checkbox } from './components'
import {
  ErrorFallbackComponent,
  ErrorDisplay,
  PartnerMisconfigurationError,
} from '@/pages/external/ExternalPremiumLogin/ErrorComponents'
import { PremiumPaymentSession } from './ExternalPremiumLogin/PremiumPaymentSession'
import { PremiumCheckoutContents } from './PremiumCheckoutContents'
import { SubscriptionRequiredComponent } from './SubscriptionRequiredComponent'
import { oauthCheckoutSessionStatus } from '@/services/checkout'
import {
  ConsentChallengeResponse,
  hasSubscriptionRequired,
  RichRequiredAction,
  Subscription,
  User,
  UserPiiPermissions,
} from '@/types'
import { ConsentScreen } from './ConsentScreen'
import { AppShell, AppButton, AppLinkButton, AppTextField, CenteredCard } from './components/shell'

type ExternalLoginMode = 'standard' | 'premium'

type ExternalLoginSession = {
  token: string
  refreshToken: string
  user: User
  subscriptions: Subscription[]
}

type ExternalLoginForm = {
  email: string
  password: string
  enableTermsAndConditions: boolean
}

export const ExternalLoginPage = ({ mode }: { mode: ExternalLoginMode }) => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallbackComponent}>
      <ExternalLoginContents mode={mode} />
    </ErrorBoundary>
  )
}

function ExternalLoginContents({ mode }: { mode: ExternalLoginMode }) {
  const { t } = useTranslation()
  const { showBoundary } = useErrorBoundary()

  // Resolve partner key from route param (standard) or search param (premium)
  const { partnerId } = useParams()
  const [searchParams] = useSearchParams()
  const clientKey = searchParams.get('clientKey')
  const partnerKey = mode === 'standard' ? (partnerId ?? null) : (clientKey ?? null)
  const verifyDuprId = searchParams.get('verifyDuprId')

  // Stripe return detection — restore session from localStorage after checkout redirect
  const isStripeReturn = searchParams.get('stripeComplete') === 'true'
  const [restoredSession] = useState<ExternalLoginSession | null>(() => {
    if (!isStripeReturn) return null
    try {
      const stored = sessionStorage.getItem('dupr_external_session')
      if (stored) return JSON.parse(stored) as ExternalLoginSession
    } catch (e) {
      console.error('[ExternalLogin] Failed to parse stored session:', e)
    }
    return null
  })

  // Session state
  const [session, setSession] = useState<ExternalLoginSession | Error | null>(restoredSession)
  const [loginError, setLoginError] = useState<string | undefined>()
  // Set when the API answers a login with a 2FA challenge instead of a session. Presence of this
  // token is what switches the card from credentials to a code box.
  const [challengeToken, setChallengeToken] = useState<string | null>(null)
  const [challengeMethods, setChallengeMethods] = useState<SecondFactorKind[]>([])
  const [usePasskey, setUsePasskey] = useState(false)
  // The "passkeys are not available here yet" notice, opened by any passkey attempt on this page.
  const [passkeyNoticeOpen, setPasskeyNoticeOpen] = useState(false)
  const [challengeCode, setChallengeCode] = useState('')
  const [subscriptionError, setSubscriptionError] = useState<boolean>(false)
  const [subscriptionRequired, setSubscriptionRequired] = useState<boolean>(false)
  // Set when the server refuses the partner login because the account still owes first-party
  // setup (a forced password reset and/or strong two-factor enrolment). Nothing was minted; the
  // user must finish that on dupr.com or in the DUPR app and then sign in here again.
  const [setupRequired, setSetupRequired] = useState<{ passwordReset: boolean; strongMfa: boolean } | null>(null)
  const [stripeCheckoutComplete, setStripeCheckoutComplete] = useState(false)

  // Form setup
  const [showLogin, setShowLogin] = useState<boolean>(false)

  const {
    handleSubmit,
    watch,
    register,
    setValue,
    formState: { errors },
  } = useForm<ExternalLoginForm>({
    defaultValues: { email: '', password: '', enableTermsAndConditions: false },
    resolver: yupResolver(ExternalSignInValidationSchema),
    mode: 'onSubmit',
  })

  const enableTermsAndConditions = watch('enableTermsAndConditions')

  const { mutate: externalLogin, isLoading: isLoginLoading } = useLoginExternalApplication()

  const { mutate: verifyTwoFactor, isLoading: isVerifying } = useVerifyTwoFactor()
  const { mutate: resendTwoFactor, isLoading: isResending } = useResendTwoFactor()
  const { mutate: fetchSubscriptions } = useOauthSubscriptions()

  const hasMethod = (urn: string) => challengeMethods.some((m) => m.type === urn)
  const isTotpChallenge = hasMethod(SECOND_FACTOR_URN.TOTP)
  // Passkeys are advertised but NOT performed on this partner surface: it is routinely embedded in
  // an iframe or a native WebView, where the credential API is refused or missing. Any passkey
  // attempt opens the notice below instead, so the link is still shown even where the browser
  // cannot do WebAuthn (that is exactly the user who needs the explanation).
  const passkeyOffered = hasMethod(SECOND_FACTOR_URN.WEBAUTHN)
  const hasCodeChallenge =
    isTotpChallenge || hasMethod(SECOND_FACTOR_URN.EMAIL) || hasMethod(SECOND_FACTOR_URN.SMS)

  // The advertised methods live in the rich `requirements` (strong factors never in the legacy
  // `twoFactorMethods`); default to email for an old server body. This partner path enforces the
  // same strong-MFA policy as the main app.
  const challengeMethodsFrom = (data: any): SecondFactorKind[] =>
    findTwoFactorChallenge(data?.requirements)?.methods ?? [{ type: SECOND_FACTOR_URN.EMAIL }]

  const beginChallenge = (token: string, data: any) => {
    const advertised = challengeMethodsFrom(data)
    setChallengeMethods(advertised)
    // A passkey-only account lands on the passkey card (whose button opens the notice) rather than
    // on a code box no code was ever sent for.
    setUsePasskey(
      advertised.some((m) => m.type === SECOND_FACTOR_URN.WEBAUTHN) &&
        !advertised.some(
          (m) =>
            m.type === SECOND_FACTOR_URN.TOTP ||
            m.type === SECOND_FACTOR_URN.EMAIL ||
            m.type === SECOND_FACTOR_URN.SMS
        )
    )
    setChallengeToken(token)
    setLoginError(undefined)
  }
  const { mutate: grantConsent, isLoading: isGrantingConsent } = useGrantConsent()

  // Consent flow state
  const [consentChallenge, setConsentChallenge] = useState<ConsentChallengeResponse | null>(null)

  const extractConsentChallenge = (error: any): ConsentChallengeResponse | null => {
    if (error?.response?.status === 428 && error?.response?.data?.consentToken) {
      return error.response.data as ConsentChallengeResponse
    }
    return null
  }

  // The login second factor is now a 428 (never a 200 with a session), so it arrives on the error
  // path. Returns the challenge token to switch into code entry, or null when this isn't that 428.
  const extractTwoFactorChallenge = (error: any): string | null => {
    if (error?.response?.status !== 428) return null
    // The challenge token comes FROM the rich two-factor-challenge requirement, not the legacy
    // top-level `challengeToken`. (Legacy fallback: requiredActions.includes('TWO_FACTOR_CHALLENGE')
    // && data.challengeToken.)
    return findTwoFactorChallenge(requirementsFrom(error))?.challengeToken ?? null
  }

  // The server refuses a partner login (428, no token minted) for an account that still owes
  // first-party setup. Returns what is owed, or null when this isn't that 428. Also matched on the
  // post-login subscriptions call, which is where an older server (or an already-minted token)
  // surfaces the strong-MFA requirement instead.
  const extractSetupRequired = (error: any): { passwordReset: boolean; strongMfa: boolean } | null => {
    if (error?.response?.status !== 428) return null
    const requirements = requirementsFrom(error)
    const passwordReset = hasPasswordReset(requirements)
    const strongMfa = hasStrongMfaSetup(requirements)
    return passwordReset || strongMfa ? { passwordReset, strongMfa } : null
  }

  const blockForSetup = (owed: { passwordReset: boolean; strongMfa: boolean }) => {
    setChallengeToken(null)
    setChallengeCode('')
    setConsentChallenge(null)
    setLoginError(undefined)
    setSetupRequired(owed)
    if (mode === 'standard') {
      // Let the embedding page know no credentials are coming, as the DUPR ID mismatch does.
      window.parent.postMessage({ error: 'DUPR account setup required' }, parentTargetOrigin())
    }
  }

  // The origin that embedded this iframe. Read-only tokens are posted to THIS specific origin, never
  // to '*', so a session can never reach an origin that isn't the partner that framed us. The
  // server's `frame-ancestors` CSP is the primary control over who may embed; this is defense in
  // depth. Falls back to '*' only when the embedder cannot be determined (e.g. a stripped referrer
  // on a browser without ancestorOrigins), which the CSP still covers.
  const parentTargetOrigin = (): string => {
    try {
      const ancestors = (window.location as unknown as { ancestorOrigins?: DOMStringList }).ancestorOrigins
      if (ancestors && ancestors.length > 0) return ancestors[0]
      if (document.referrer) return new URL(document.referrer).origin
    } catch {
      /* fall through to wildcard */
    }
    return '*'
  }

  const autoGrantConsent = (consent: ConsentChallengeResponse) => {
    grantConsent(
      {
        consentToken: consent.consentToken,
        permissions: consent.existingPermissions ?? ({} as UserPiiPermissions),
        useExistingConsent: true,
      },
      {
        onSuccess: (data) => {
          completeLogin(
            data.result.accessToken,
            data.result.refreshToken,
            data.result.user,
            data.result.requirements
          )
        },
        onError: (error: any) => {
          const owed = extractSetupRequired(error)
          if (owed) {
            blockForSetup(owed)
            return
          }
          // Auto-grant failed — fall back to showing consent screen
          setConsentChallenge(consent)
        },
      }
    )
  }

  // Track login success
  useEffect(() => {
    if (session && !(session instanceof Error)) {
      eventTracker(TRACKING_TYPES.PARTNER_LOGIN_SUCCESS, {
        'Page Name': PAGE_NAMES.PARTNER_LOGIN,
        'User ID': session.user.id,
        Email: session.user.email,
        Name: session.user.fullName,
      })
    }
  }, [session])

  // Handle Stripe return — verify session status, postMessage on success
  const stripeSessionId = searchParams.get('session_id')
  const [stripeError, setStripeError] = useState<string | undefined>()

  useEffect(() => {
    if (!isStripeReturn || !restoredSession || !stripeSessionId) return

    oauthCheckoutSessionStatus({ token: restoredSession.token, sessionId: stripeSessionId })
      .then((result) => {
        if (result.status === 'complete') {
          trackPurchaseCompleted({
            purchase_flow: 'partner_stripe',
            partner_id: partnerKey ?? undefined,
            stripe_session_id: stripeSessionId,
          })
          fetchSubscriptions(
            { token: restoredSession.token },
            {
              onSuccess: (res) => {
                console.log('[ExternalLogin] Posting credentials to parent window (Stripe return)', {
                  subscriptions: res.subscriptions,
                })
                window.parent.postMessage(
                  {
                    userToken: restoredSession.token,
                    refreshToken: restoredSession.refreshToken,
                    id: restoredSession.user.id,
                    duprId: restoredSession.user.referralCode,
                    stats: restoredSession.user.stats,
                    subscriptions: res.subscriptions,
                  },
                  parentTargetOrigin()
                )
                setStripeCheckoutComplete(true)
              },
              onError: () => {
                console.log(
                  '[ExternalLogin] Posting credentials to parent window (Stripe return, subscription fetch failed)',
                  { subscriptions: [] }
                )
                window.parent.postMessage(
                  {
                    userToken: restoredSession.token,
                    refreshToken: restoredSession.refreshToken,
                    id: restoredSession.user.id,
                    duprId: restoredSession.user.referralCode,
                    stats: restoredSession.user.stats,
                    subscriptions: [],
                  },
                  parentTargetOrigin()
                )
                setStripeCheckoutComplete(true)
              },
            }
          )
        } else {
          console.error('[ExternalLogin] Stripe session not complete:', result.status, result.paymentStatus)
          setStripeError('Payment was not completed. Please try again.')
        }
      })
      .catch((error) => {
        console.error('Failed to verify Stripe session status:', error)
        setStripeError('Unable to verify payment status. Please try again.')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (session instanceof Error) {
    showBoundary(session)
  }

  // Shared login completion: fetch subscriptions, optionally postMessage, set session
  const completeLogin = (accessToken: string, refreshToken: string, user: User, requirements?: RichRequiredAction[]) => {
    // Subscription required — skip normal flow, show subscription prompt. Driven by the rich
    // `requirements` (the subscription-required URN). Legacy fallback (if a 200 login body ever
    // carries only the flat field): requiredActions?.includes('SUBSCRIPTION_REQUIRED').
    if (hasSubscriptionRequired(requirements)) {
      setSession({ token: accessToken, refreshToken, user, subscriptions: [] })
      setSubscriptionRequired(true)
      return
    }

    // DUPR ID verification
    if (verifyDuprId !== null && verifyDuprId !== user.referralCode) {
      setSession(
        new PartnerMisconfigurationError(`DUPR ID Mismatch:
The DUPR ID you logged in with is not the one linked by your partner.
This may be due to logging into the wrong account, or a setup issue on your partner's side.
Please contact your partner's support team for help.`)
      )
      window.parent.postMessage(
        {
          error: 'User ID mismatch',
          userId: user.id,
          expectedId: verifyDuprId,
        },
        parentTargetOrigin()
      )
      return
    }

    fetchSubscriptions(
      { token: accessToken },
      {
        onSuccess: (res) => {
          // In standard mode, notify parent window
          if (mode === 'standard') {
            console.log('[ExternalLogin] Posting credentials to parent window')
            window.parent.postMessage(
              {
                userToken: accessToken,
                refreshToken: refreshToken,
                id: user.id,
                duprId: user.referralCode,
                stats: user.stats,
                subscriptions: res.subscriptions,
              },
              parentTargetOrigin()
            )
          }
          setSession({ token: accessToken, refreshToken, user, subscriptions: res.subscriptions })
        },
        onError: (error: any) => {
          console.error('Failed to fetch subscriptions after login:', error)
          // A token minted for an account that owes strong-MFA setup is 428'd here (an older
          // server, or a token minted before the login-time refusal): the same block, with no
          // credentials posted, rather than a misleading "temporary issue".
          const owed = extractSetupRequired(error)
          if (owed) {
            blockForSetup(owed)
            return
          }
          if (mode === 'standard') {
            setSubscriptionError(true)
          } else {
            // Premium mode: proceed with empty subscriptions so checkout can still render
            setSession({ token: accessToken, refreshToken, user, subscriptions: [] })
          }
        },
      }
    )
  }

  // Form submit handler
  const onSubmit = handleSubmit((data) => {
    if (partnerKey) {
      const payload = { email: data.email.trim().toLocaleLowerCase(), password: data.password, partnerId: partnerKey }
      externalLogin(payload, {
        onSuccess: (data) => {
          // A 200 does not always mean a session: with the email second factor on, login answers
          // with a challenge and no accessToken. Reading accessToken unconditionally here is what
          // breaks a client that has not been taught about the interstitial.
          if (data.result?.twoFactorRequired && data.result?.challengeToken) {
            beginChallenge(data.result.challengeToken, data.result)
            return
          }
          completeLogin(
            data.result.accessToken,
            data.result.refreshToken,
            data.result.user,
            data.result.requirements
          )
        },
        onError: (error: any) => {
          const twoFa = extractTwoFactorChallenge(error)
          if (twoFa) {
            beginChallenge(twoFa, error?.response?.data)
            return
          }
          const consent = extractConsentChallenge(error)
          if (consent) {
            if (consent.consentAlreadyGranted) {
              autoGrantConsent(consent)
              return
            }
            setConsentChallenge(consent)
            return
          }
          const owed = extractSetupRequired(error)
          if (owed) {
            blockForSetup(owed)
            return
          }

          if (error?.response?.data?.message) {
            const errorMessage = error.response.data.message
            setLoginError(errorMessage)
            eventTracker(TRACKING_TYPES.PARTNER_LOGIN_ERROR, { errorMessage, 'Page Name': PAGE_NAMES.PARTNER_LOGIN })
          } else if (error?.code === 'ECONNABORTED') {
            setLoginError(APP_TIMEOUT_ERROR)
          } else if (typeof error?.response?.data === 'string') {
            setLoginError(error.response.data)
          } else {
            setLoginError(APP_CUSTOM_ERROR)
          }
        },
      })
    }
  })

  // Exchanges the emailed code for a real session. The response shape is the same as an ordinary
  // login, so this finishes through the same completeLogin path.
  const challengeSuccess = (data: any) =>
    completeLogin(data.result.accessToken, data.result.refreshToken, data.result.user, data.result.requirements)

  const challengeError = (error: any) => {
    // A correct factor on an account that owes first-party setup is refused outright.
    const owed = extractSetupRequired(error)
    if (owed) {
      blockForSetup(owed)
      return
    }
    // With consent on, a correct factor is answered with the consent challenge (the second partial
    // state), not a session. Route that into the consent screen; anything else is a real error.
    const consent = extractConsentChallenge(error)
    if (consent) {
      if (consent.consentAlreadyGranted) {
        autoGrantConsent(consent)
        return
      }
      setConsentChallenge(consent)
      return
    }
    setLoginError(error?.response?.data?.message ?? APP_CUSTOM_ERROR)
  }

  const onSubmitChallenge = (event: React.FormEvent) => {
    event.preventDefault()
    if (!challengeToken) return
    if (usePasskey) {
      setLoginError(undefined)
      setPasskeyNoticeOpen(true)
      return
    }
    if (challengeCode.trim().length === 0) return
    verifyTwoFactor(
      // Strong factor -> send the kind; legacy delivered code -> omit it.
      { challengeToken, code: challengeCode.trim(), method: isTotpChallenge ? { type: SECOND_FACTOR_URN.TOTP } : undefined },
      { onSuccess: challengeSuccess, onError: challengeError }
    )
  }

  // --- Render ---

  // Stripe checkout return — show success
  if (stripeCheckoutComplete) {
    return (
      <AppShell logoLinksToDashboard={false}>
        <CenteredCard>
          <p className="text-center text-[color:var(--ink-soft)] text-sm mb-2">Connected with</p>
          <p className="text-center text-[color:var(--ink)] text-3xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] mb-6">
            DUPR
          </p>
          <StandardSuccessComponent
            type="payment"
            message="Your DUPR subscription is active, you may now close this window"
            showProductEligibility={false}
            base64ClientKey={partnerKey!}
          />
        </CenteredCard>
      </AppShell>
    )
  }

  // Stripe return — payment verification failed
  if (stripeError) {
    return <ErrorDisplay message={stripeError} error={new Error('Stripe payment verification failed')} />
  }

  // Stripe return in progress — show loading
  if (isStripeReturn && restoredSession && stripeSessionId) {
    return (
      <AppShell logoLinksToDashboard={false}>
        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[color:var(--accent)] mb-4" />
          <p className="text-[color:var(--ink-soft)] text-sm">Verifying your payment...</p>
        </div>
      </AppShell>
    )
  }

  // Stripe return but missing session or session ID — show error
  if (isStripeReturn) {
    return (
      <ErrorDisplay
        message={
          !restoredSession
            ? 'Your session has expired. Please log in again to verify your payment.'
            : 'Payment session ID is missing. Please try again.'
        }
        error={new Error('Stripe return missing session data')}
      />
    )
  }

  // The account owes first-party setup: the partner login was refused and nothing was minted. The
  // remedy lives on dupr.com / in the DUPR app, never inside the partner's frame.
  if (setupRequired) {
    const steps = [
      setupRequired.passwordReset && t('external.setup.resetPassword', 'Set a new password for your account'),
      setupRequired.strongMfa &&
        t('external.setup.strongMfa', 'Set up two-factor authentication with an authenticator app or a passkey'),
    ].filter((s): s is string => !!s)
    const remedyPath = setupRequired.passwordReset ? ROUTE_FORGOT_PASSWORD : ROUTE_TWO_FACTOR
    return (
      <AppShell logoLinksToDashboard={false}>
        <CenteredCard>
          <div className="flex flex-col items-center gap-y-4 w-full text-center">
            <p className="text-xs uppercase tracking-widest text-[color:var(--ink-faint)]">
              {t('common.connectWith')} DUPR
            </p>
            <h1 className="text-2xl md:text-3xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)]">
              {t('external.setup.title', 'Finish setting up your DUPR account')}
            </h1>
            <p className="text-sm text-[color:var(--ink-soft)]">
              {t(
                'external.setup.body',
                'Before you can sign in with DUPR here, your account needs some additional setup:'
              )}
            </p>
            <ul className="text-sm text-[color:var(--ink)] text-left list-disc pl-6 self-stretch">
              {steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
            <p className="text-sm text-[color:var(--ink-soft)]">
              {t(
                'external.setup.where',
                'Do this on dupr.com or in the DUPR app, then come back here and sign in again.'
              )}
            </p>
            <AppLinkButton href={`${BASE_APP_URL}${remedyPath}`} target="_blank" rel="noopener noreferrer" fullWidth>
              {t('external.setup.openDupr', 'Open DUPR')}
            </AppLinkButton>
            <button
              type="button"
              onClick={() => setSetupRequired(null)}
              className="text-[color:var(--ink-faint)] text-xs hover:text-[color:var(--ink-soft)] transition-colors"
            >
              {t('external.setup.backToSignIn', 'Back to sign in')}
            </button>
          </div>
        </CenteredCard>
      </AppShell>
    )
  }

  // Subscription fetch error (standard mode)
  if (subscriptionError) {
    return (
      <ErrorDisplay
        message="We're experiencing a temporary issue. Your login was successful, but we couldn't load your account details. Please close this window and try again shortly."
        error={new Error('Subscription details could not be retrieved')}
      />
    )
  }

  // Subscription required — show purchase flow, withhold credentials until checkout succeeds
  if (subscriptionRequired && session && !(session instanceof Error)) {
    return (
      <AppShell logoLinksToDashboard={false}>
        <CenteredCard>
          <SubscriptionRequiredComponent token={session.token} base64ClientKey={partnerKey!} user={session.user} />
        </CenteredCard>
      </AppShell>
    )
  }

  // Consent auto-grant in progress — show loading
  if (isGrantingConsent) {
    return (
      <AppShell logoLinksToDashboard={false}>
        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[color:var(--accent)] mb-4" />
          <p className="text-[color:var(--ink-soft)] text-sm">Connecting...</p>
        </div>
      </AppShell>
    )
  }

  // Consent required — show permission consent screen
  if (consentChallenge) {
    return (
      <ConsentScreen
        challenge={consentChallenge}
        onComplete={(accessToken, refreshToken, user, requirements) => {
          setConsentChallenge(null)
          completeLogin(accessToken, refreshToken, user, requirements)
        }}
        onError={(message, error) => {
          const owed = extractSetupRequired(error)
          if (owed) {
            blockForSetup(owed)
            return
          }
          setConsentChallenge(null)
          setLoginError(message)
        }}
        onCancel={() => {
          setConsentChallenge(null)
        }}
      />
    )
  }

  // Post-login: premium mode → persist session for Stripe return, show checkout (skip if returning from Stripe)
  if (session && !(session instanceof Error) && mode === 'premium' && !isStripeReturn) {
    sessionStorage.setItem('dupr_external_session', JSON.stringify(session))
    return (
      <PremiumPaymentSession token={session.token} base64ClientKey={partnerKey!} user={session.user}>
        <PremiumCheckoutContents
          token={session.token}
          refreshToken={session.refreshToken}
          clientKey={partnerKey!}
          user={session.user}
          subscriptions={session.subscriptions}
        />
      </PremiumPaymentSession>
    )
  }

  // Post-login: standard mode → show success with upsell
  if (session && !(session instanceof Error) && mode === 'standard') {
    return <SuccessMessage token={session.token} user={session.user} base64ClientKey={partnerKey!} />
  }

  // Two-factor code entry. Rendered instead of the credentials card once login has answered with a
  // challenge, so the user stays in one flow rather than being sent to their inbox and back.
  if (challengeToken) {
    return (
      <AppShell logoLinksToDashboard={false}>
        <CenteredCard>
          <form onSubmit={onSubmitChallenge} className="flex flex-col w-full gap-y-5">
            <div className="text-center">
              <p className="text-xs uppercase tracking-widest text-[color:var(--ink-faint)] mb-2">
                {t('common.connectWith')} DUPR
              </p>
              <h1 className="text-2xl md:text-3xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)]">
                {usePasskey
                  ? t('twoFactor.usePasskeyTitle', 'Use your passkey')
                  : isTotpChallenge
                    ? t('twoFactor.authTitle', 'Enter your authenticator code')
                    : t('twoFactor.title', 'Check your email')}
              </h1>
              <p className="text-sm text-[color:var(--ink-soft)] mt-2">
                {usePasskey
                  ? t('twoFactor.usePasskeySubtitle', 'Use your device fingerprint, face, or screen lock to sign in.')
                  : isTotpChallenge
                    ? t('twoFactor.authSubtitle', 'Enter the 6-digit code from your authenticator app.')
                    : t('twoFactor.subtitle', 'Enter the 6-digit code we just sent you.')}
              </p>
            </div>

            {!usePasskey && (
              <input
                value={challengeCode}
                onChange={(e) => setChallengeCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                aria-label={
                  isTotpChallenge
                    ? t('twoFactor.authCodeLabel', 'Authenticator code')
                    : t('twoFactor.codeLabel', 'Verification code')
                }
                className="w-full text-center tracking-[0.5em] text-2xl py-3 rounded-lg border border-[color:var(--line)] bg-transparent text-[color:var(--ink)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]"
              />
            )}

            {loginError && <p className="text-sm text-center text-[color:var(--danger,#c0392b)]">{loginError}</p>}

            <div className="flex flex-col items-stretch w-full gap-y-2">
              {usePasskey ? (
                <AppButton type="submit" fullWidth>
                  {t('twoFactor.usePasskey', 'Use a passkey')}
                </AppButton>
              ) : (
                <AppButton type="submit" fullWidth disabled={challengeCode.length < 6 || isVerifying} loading={isVerifying}>
                  <span className="inline-flex items-center gap-x-2">
                    <span>{t('common.continue', 'Continue')}</span>
                    {isVerifying && <Spinner className="w-4 h-4 fill-white border:none" />}
                  </span>
                </AppButton>
              )}
              {passkeyOffered && hasCodeChallenge && (
                <button
                  type="button"
                  // Towards the passkey: the notice. Back towards a code: the ordinary toggle.
                  onClick={() => (usePasskey ? setUsePasskey(false) : setPasskeyNoticeOpen(true))}
                  className="text-center text-[color:var(--ink-soft)] hover:text-[color:var(--ink)] text-sm font-medium py-2 transition-colors"
                >
                  {usePasskey
                    ? t('twoFactor.useCodeInstead', 'Use a code instead')
                    : t('twoFactor.usePasskeyInstead', 'Use a passkey instead')}
                </button>
              )}
              {/* A resend only makes sense for a delivered code; the server refuses it once a strong factor is enrolled. */}
              {!usePasskey && !isTotpChallenge && (
                <button
                  type="button"
                  disabled={isResending}
                  onClick={() => {
                    setLoginError(undefined)
                    resendTwoFactor({ challengeToken })
                  }}
                  className="text-center text-[color:var(--ink-soft)] hover:text-[color:var(--ink)] text-sm font-medium py-2 transition-colors"
                >
                  {t('twoFactor.resend', 'Send a new code')}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  // Back to credentials. The challenge token is dropped rather than kept, so a
                  // second attempt starts a fresh one and a stale code cannot be replayed.
                  setChallengeToken(null)
                  setChallengeCode('')
                  setChallengeMethods([])
                  setUsePasskey(false)
                  setLoginError(undefined)
                }}
                className="text-center text-[color:var(--ink-faint)] hover:text-[color:var(--ink)] text-sm py-1 transition-colors"
              >
                {t('common.back', 'Back')}
              </button>
            </div>
          </form>
        </CenteredCard>

        <Dialog open={passkeyNoticeOpen} onClose={() => setPasskeyNoticeOpen(false)} className="relative z-50">
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-md rounded-[var(--radius-card,1rem)] bg-[color:var(--surface,#fff)] p-6 shadow-xl flex flex-col gap-y-4 text-center">
              <Dialog.Title className="text-xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)]">
                {t('twoFactor.passkeyUnavailableTitle', 'Passkeys are not available here yet')}
              </Dialog.Title>
              <Dialog.Description className="text-sm text-[color:var(--ink-soft)]">
                {t(
                  'twoFactor.passkeyUnavailableBody',
                  'Passkeys are not currently available in the partner sign-in flow. For now, please set up an authenticator app on your DUPR account and use its code to sign in here.'
                )}
              </Dialog.Description>
              <AppLinkButton href={`${BASE_APP_URL}${ROUTE_TWO_FACTOR}`} target="_blank" rel="noopener noreferrer" fullWidth>
                {t('twoFactor.setUpAuthenticator', 'Set up an authenticator app')}
              </AppLinkButton>
              <AppButton type="button" variant="secondary" fullWidth onClick={() => setPasskeyNoticeOpen(false)}>
                {t('common.close', 'Close')}
              </AppButton>
            </Dialog.Panel>
          </div>
        </Dialog>
      </AppShell>
    )
  }

  // Login form
  return (
    <AppShell logoLinksToDashboard={false}>
      <CenteredCard>
        <form onSubmit={onSubmit} className="flex flex-col w-full gap-y-5">
          <div className="text-center">
            <p className="text-xs uppercase tracking-widest text-[color:var(--ink-faint)] mb-2">
              {t('common.connectWith')} DUPR
            </p>
            <h1 className="text-2xl md:text-3xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)]">
              {t('common.externalApplication')}
            </h1>
          </div>

          {!showLogin ? (
            <div className="flex flex-col items-stretch w-full gap-y-3 mt-2">
              <AppButton
                fullWidth
                onClick={() => {
                  setShowLogin(true)
                }}
              >
                {t('auth.signIn')}
              </AppButton>

              <a
                href={`${BASE_APP_URL}/signup`}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-[color:var(--accent)] text-[color:var(--accent)] text-base font-semibold w-full h-14 inline-flex items-center justify-center rounded-[var(--radius-button)] hover:bg-[color:var(--accent-soft)] transition-colors"
              >
                {t('auth.signUp')}
              </a>

              <p className="text-[color:var(--ink-soft)] text-sm text-center mt-2">
                {t('auth.claimQuestion')}{' '}
                <a
                  href={`${BASE_APP_URL}/claim`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-[color:var(--accent)] hover:opacity-70"
                >
                  {t('auth.claimAccount')}
                </a>
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-y-4">
              <a
                href={`${BASE_APP_URL}/signup`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[color:var(--ink-faint)] text-xs text-right hover:text-[color:var(--ink-soft)] transition-colors"
              >
                ← I don't have an account
              </a>
              {loginError && (
                <p
                  className="text-sm text-[color:var(--negative)] bg-[color:var(--negative)]/8 border border-[color:var(--negative)]/20 rounded-[var(--radius-input)] px-3 py-2"
                  role="alert"
                >
                  {loginError}
                </p>
              )}

              <AppTextField
                label={t('common.email')}
                placeholder={t('common.emailPlaceHolder')}
                error={errors.email?.message}
                variant="email"
                autoComplete="email"
                {...register('email')}
              />

              <AppTextField
                label={t('common.password')}
                placeholder={t('common.passwordPlaceHolder')}
                error={errors.password?.message}
                variant="password"
                autoComplete="current-password"
                {...register('password')}
              />

              <Checkbox
                label={t('common.externalLoginCheckbox')}
                value={enableTermsAndConditions}
                labelClassName="text-xs text-[color:var(--ink-soft)]"
                setValue={(val) => setValue('enableTermsAndConditions', val)}
              />

              <div className="flex flex-col items-stretch w-full gap-y-2 mt-2">
                <AppButton
                  type="submit"
                  fullWidth
                  disabled={!enableTermsAndConditions || isLoginLoading}
                  loading={isLoginLoading}
                  onClick={() => {
                    eventTracker(TRACKING_TYPES.CONNECTED_ACCOUNT, {
                      partner_id: partnerKey,
                    })
                  }}
                >
                  <span className="inline-flex items-center gap-x-2">
                    <span>{isLoginLoading ? t('common.logging') : t('common.login')}</span>
                    {isLoginLoading && <Spinner className="w-4 h-4 fill-white border:none" />}
                  </span>
                </AppButton>
                <a
                  href={`${BASE_APP_URL}${ROUTE_FORGOT_PASSWORD}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-center text-[color:var(--ink-soft)] hover:text-[color:var(--ink)] text-sm font-medium py-2 transition-colors"
                >
                  {t('common.forgotPassword')}?
                </a>
              </div>
            </div>
          )}
        </form>
      </CenteredCard>
    </AppShell>
  )
}

function SuccessMessage({
  token,
  user,
  base64ClientKey,
}: {
  token?: string | null
  user?: User
  base64ClientKey: string
}) {
  const [searchParams] = useSearchParams()
  const redirect = searchParams.get('redirect')
  const [redeemCtx, setRedeemCtx] = useState<ProceedToRedeemContext | null>(null)

  if (redirect !== null) {
    // Inside a partner iframe / in-app WebView the full app must never render in
    // the embed — offer a top-level break-out tab instead. The credentials were
    // already posted to the host before this renders, so the handshake is done.
    if (isEmbeddedContext()) {
      return <EmbeddedRedirectBreakout redirect={redirect} />
    }
    return <Navigate to={redirect} />
  }

  // Render the redeem flow at the top level so its own AppShell + max-w-5xl
  // two-column layout isn't squeezed inside the success card's max-w-md surface.
  if (redeemCtx) {
    if (redeemCtx.apiError || !redeemCtx.eligiblePromotions.length) {
      return (
        <PremiumPaymentSession
          token={redeemCtx.token || ''}
          base64ClientKey={redeemCtx.base64ClientKey}
          user={redeemCtx.user}
        >
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-tr from-[#05155E] from-15% via-[#0163D0] to-[#4B97FE] p-4">
            <div className="w-full max-w-md">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gradient-to-tr from-[#05155E] from-15% via-[#0163D0] to-[#4B97FE] p-9">
                  <p className="text-center text-white text-sm">Connect With</p>
                  <p className="text-center text-white text-[40px] font-black tracking-[-4px]">DUPR</p>
                </div>
                <div className="p-6">
                  <RevenueCatPurchase isModal={true} />
                </div>
              </div>
            </div>
          </div>
        </PremiumPaymentSession>
      )
    }
    return (
      <ExternalRedeemModal
        token={redeemCtx.token}
        base64ClientKey={redeemCtx.base64ClientKey}
        promotionIds={redeemCtx.eligiblePromotions}
        user={redeemCtx.user}
      />
    )
  }

  return (
    <AppShell logoLinksToDashboard={false}>
      <CenteredCard>
        <p className="text-center text-[color:var(--ink-soft)] text-sm mb-2">Connected with</p>
        <p className="text-center text-[color:var(--ink)] text-3xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] mb-6">
          DUPR
        </p>
        <StandardSuccessComponent
          type="login"
          message="Login with DUPR Complete you can close this modal now"
          showProductEligibility={true}
          token={token || undefined}
          base64ClientKey={base64ClientKey}
          user={user}
          onProceedToRedeem={setRedeemCtx}
        />
      </CenteredCard>
    </AppShell>
  )
}

/**
 * Post-login stop for embedded SSO: parks the partner iframe / in-app WebView on a
 * success card and opens the partner's `redirect` target as a top-level browser tab,
 * instead of rendering the full DUPR app inside the embed. The redirect is treated
 * strictly as an app path (never an absolute URL), so the break-out can only land
 * on DUPR's own domain.
 */
function EmbeddedRedirectBreakout({ redirect }: { redirect: string }) {
  const appPath = redirect.startsWith('/') ? redirect : `/${redirect}`
  return (
    <AppShell logoLinksToDashboard={false}>
      <CenteredCard>
        <div className="flex flex-col items-center gap-y-4 w-full text-center">
          <p className="text-xs uppercase tracking-widest text-[color:var(--ink-faint)]">Connected with DUPR</p>
          <p className="text-[color:var(--ink)] font-semibold text-lg">You&rsquo;re signed in</p>
          <p className="text-[color:var(--ink-soft)] text-sm">
            To keep going, open DUPR in your browser. You can close this window afterwards.
          </p>
          <AppLinkButton href={`${BASE_APP_URL}${appPath}`} target="_blank" rel="noopener noreferrer" fullWidth>
            Open DUPR in browser
          </AppLinkButton>
        </div>
      </CenteredCard>
    </AppShell>
  )
}
